import React, { useEffect, useState } from 'react';
import { Typography, Tooltip } from '@mui/material';

/**
 * Small "updated N seconds ago" label. Accepts a timestamp (number, ms
 * since epoch) or a Date. Re-renders itself on a 1s tick so the label
 * stays honest without the consuming page having to wire anything up.
 *
 * Purely presentational — does not affect data flow.
 */
function LastUpdated({ timestamp, prefix = 'updated' }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!timestamp) {
    return (
      <Typography variant="caption" color="text.secondary">
        No data yet
      </Typography>
    );
  }

  const tsMs = timestamp instanceof Date ? timestamp.getTime() : Number(timestamp);
  const ageMs = Math.max(0, now - tsMs);
  const ageSec = Math.round(ageMs / 1000);

  let label;
  if (ageSec < 5) label = 'just now';
  else if (ageSec < 60) label = `${ageSec}s ago`;
  else if (ageSec < 3600) label = `${Math.floor(ageSec / 60)}m ago`;
  else label = `${Math.floor(ageSec / 3600)}h ago`;

  const stale = ageMs > 60_000;

  return (
    <Tooltip title={new Date(tsMs).toLocaleString()} arrow>
      <Typography
        variant="caption"
        sx={{
          color: stale ? 'warning.main' : 'text.secondary',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {prefix} {label}
      </Typography>
    </Tooltip>
  );
}

export default LastUpdated;
